import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'i18n';

/**
 * Product gallery: the image fills a fixed square frame (object-contain, no
 * empty grey bars), vertical thumbnails on desktop, swipe + dots on mobile,
 * hover zoom only on devices that can hover, tap/click -> fullscreen
 * lightbox where the browser's own pinch-zoom works. Thumbnails lazy-load.
 */
export default function ProductGallery({ images = [], alt = '', active = 0, onChange }) {
  const { t } = useTranslation();
  const [zoom, setZoom] = useState(null); // "x% y%" while hovering (desktop)
  const [lightbox, setLightbox] = useState(false);
  const touch = useRef(null);
  const count = images.length;
  const idx = Math.min(active, Math.max(0, count - 1));
  const go = (i) => onChange?.((i + count) % count);

  useEffect(() => {
    if (!lightbox) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setLightbox(false);
      if (e.key === 'ArrowRight') go(idx + 1);
      if (e.key === 'ArrowLeft') go(idx - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const canHover = typeof window !== 'undefined' && window.matchMedia?.('(hover: hover) and (pointer: fine)').matches;

  if (!count) return <div className="aspect-square w-full rounded-2xl bg-surface-2" aria-hidden />;

  return (
    <div className="grid gap-3 md:grid-cols-[72px_minmax(0,1fr)]" data-testid="product-gallery">
      <div className="order-2 hidden md:order-1 md:flex md:max-h-[560px] md:flex-col md:gap-2 md:overflow-y-auto" role="list">
        {images.map((src, i) => (
          <button
            key={`${src}-${i}`}
            type="button"
            role="listitem"
            onClick={() => go(i)}
            aria-label={t('productPage.viewImage', { n: i + 1 })}
            aria-current={i === idx}
            className={`h-[72px] w-[72px] shrink-0 overflow-hidden rounded-xl border-2 bg-surface p-1 ${i === idx ? 'border-primary' : 'border-border hover:border-muted'}`}
          >
            <img src={src} alt="" loading="lazy" width="64" height="64" referrerPolicy="no-referrer" className="h-full w-full object-contain" />
          </button>
        ))}
      </div>

      <div className="order-1 md:order-2">
        <div
          className="relative aspect-square w-full overflow-hidden rounded-2xl border border-border bg-white"
          onMouseMove={canHover ? (e) => {
            const r = e.currentTarget.getBoundingClientRect();
            setZoom(`${((e.clientX - r.left) / r.width) * 100}% ${((e.clientY - r.top) / r.height) * 100}%`);
          } : undefined}
          onMouseLeave={() => setZoom(null)}
          onTouchStart={(e) => { touch.current = e.touches[0].clientX; }}
          onTouchEnd={(e) => {
            if (touch.current == null) return;
            const dx = e.changedTouches[0].clientX - touch.current;
            touch.current = null;
            if (Math.abs(dx) > 40) {
              const rtl = document.documentElement.dir === 'rtl';
              go(idx + ((dx < 0) !== rtl ? 1 : -1));
            }
          }}
        >
          <button type="button" className="block h-full w-full cursor-zoom-in" onClick={() => setLightbox(true)}
            aria-label={t('gallery.openFullscreen')} data-testid="gallery-main">
            <img
              key={images[idx]}
              src={images[idx]}
              alt={alt}
              width="800"
              height="800"
              decoding="async"
              referrerPolicy="no-referrer"
              className="h-full w-full object-contain transition-transform duration-200"
              style={zoom ? { transform: 'scale(1.8)', transformOrigin: zoom } : undefined}
            />
          </button>
        </div>
        {count > 1 && (
          <div className="mt-3 flex justify-center gap-1.5 md:hidden" role="tablist" aria-label={t('gallery.images')}>
            {images.map((src, i) => (
              <button key={`dot-${src}-${i}`} type="button" role="tab" aria-selected={i === idx}
                aria-label={t('productPage.viewImage', { n: i + 1 })} onClick={() => go(i)}
                className={`h-2 rounded-full transition-all ${i === idx ? 'w-5 bg-primary' : 'w-2 bg-border'}`} />
            ))}
          </div>
        )}
      </div>

      {lightbox && (
        <div className="fixed inset-0 z-[1400] flex flex-col bg-black/95" role="dialog" aria-modal="true" aria-label={t('gallery.images')}
          data-testid="gallery-lightbox">
          <div className="flex items-center justify-between p-3 text-white">
            <span className="text-sm">{idx + 1} / {count}</span>
            <button type="button" onClick={() => setLightbox(false)} className="rounded-full px-3 py-1 text-2xl" aria-label={t('common.close')}>×</button>
          </div>
          <div className="flex min-h-0 flex-1 items-center justify-center overflow-auto" style={{ touchAction: 'pinch-zoom' }}>
            <img src={images[idx]} alt={alt} referrerPolicy="no-referrer" className="max-h-full max-w-full object-contain" />
          </div>
          {count > 1 && (
            <div className="flex justify-center gap-6 p-4 text-3xl text-white">
              <button type="button" onClick={() => go(idx - 1)} aria-label={t('gallery.previous')} className="rtl:-scale-x-100">‹</button>
              <button type="button" onClick={() => go(idx + 1)} aria-label={t('gallery.next')} className="rtl:-scale-x-100">›</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
